/**
 * PWCFormula — typeset Position-Weighted Citation formula as bespoke SVG with a
 * summation, weighted terms, and a legend. i18n labels; role="img" + <title>.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface PWCFormulaProps {
  namespace?: string;
  className?: string;
}

export function PWCFormula({ namespace = 'marketing.diagrams.pwcFormula', className }: PWCFormulaProps) {
  const t = useTranslations(namespace);
  const titleId = 'pwc-formula-title';
  return (
    <MotionReveal direction="fade" className="min-w-0">
      <figure className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <svg
          viewBox="0 0 400 120"
          role="img"
          aria-labelledby={titleId}
          className="mt-4 w-full max-w-lg"
        >
          <title id={titleId}>{t('title')}</title>
          {/* PWC = */}
          <text x="6" y="68" fill="#1aabff" fontSize="30" fontWeight="700" fontFamily="var(--font-comfortaa)">
            {t('terms.pwc')}
          </text>
          <text x="92" y="68" fill="#eeeeee" fontSize="28">
            =
          </text>
          {/* Σ */}
          <text x="120" y="74" fill="#4eb6ec" fontSize="48" fontFamily="var(--font-comfortaa)">
            {t('terms.sum')}
          </text>
          <text x="118" y="100" fill="#eeeeee" opacity="0.6" fontSize="12">
            i=1
          </text>
          <text x="124" y="34" fill="#eeeeee" opacity="0.6" fontSize="12">
            n
          </text>
          {/* wᵢ · cᵢ */}
          <text x="178" y="68" fill="#ffffff" fontSize="26" fontWeight="600" fontFamily="var(--font-inter)">
            {t('terms.weight')}
          </text>
          <text x="214" y="66" fill="#eeeeee" fontSize="22">
            ·
          </text>
          <text x="230" y="68" fill="#ffffff" fontSize="26" fontWeight="600" fontFamily="var(--font-inter)">
            {t('terms.cite')}
          </text>
          {/* animated underline */}
          <line x1="178" y1="84" x2="262" y2="84" stroke="url(#pwc-grad)" strokeWidth="2" strokeDasharray="84" strokeDashoffset="84">
            <animate attributeName="stroke-dashoffset" from="84" to="0" dur="0.9s" fill="freeze" />
          </line>
          <defs>
            <linearGradient id="pwc-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1aabff" />
              <stop offset="100%" stopColor="#0d8edb" />
            </linearGradient>
          </defs>
        </svg>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div className="flex gap-2">
            <dt className="font-semibold text-cyan-300">{t('terms.weight')}</dt>
            <dd className="text-white/70">{t('terms.weightLabel')}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-semibold text-cyan-300">{t('terms.cite')}</dt>
            <dd className="text-white/70">{t('terms.citeLabel')}</dd>
          </div>
        </dl>
        <figcaption className="mt-3 text-sm leading-relaxed text-white/60">{t('note')}</figcaption>
      </figure>
    </MotionReveal>
  );
}
